import { useEffect } from "react";

import { create } from "zustand";
import { persist } from "zustand/middleware";

import { DEVELOPMENT_ENV } from "@constants/common";

import { getEnv } from "@utils/env";

type Theme = "light" | "dark";

type ThemeState = {
  theme: Theme;
  toggle: () => void;
  setTheme: (theme: Theme) => void;
};

const isDev = getEnv("VITE_ENV") === DEVELOPMENT_ENV;

export const useTheme = create<ThemeState>()(
  persist(
    (set) => ({
      theme: "light",
      toggle: () =>
        set((state) => ({
          theme: state.theme === "light" ? "dark" : "light",
        })),
      setTheme: (theme: Theme) => set({ theme }),
    }),
    {
      name: "theme-storage",
    }
  )
);

/**
 * Syncs the Zustand theme state to the <html> element's class list.
 * Forces light mode in non-dev environments.
 * Mount this once near the app root.
 */
export const useThemeSync = () => {
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    if (!isDev && theme === "dark") {
      setTheme("light");
      return;
    }

    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [theme, setTheme]);
};
